// Formatting that follows the interface language: dates, weekdays, "5 min ago".
// Uses the browser's built-in Intl data, so there is nothing to translate by hand.

/**
 * The device's regional form of a language, when it has one: Vale's "en" on an
 * Australian phone formats dates as en-AU ("Friday 9 October", not
 * "Friday, October 9"). Falls back to the language itself.
 */
export const regional = (lng) => {
  const base = String(lng || "").split(/[-_]/)[0].toLowerCase();
  const nav = typeof navigator === "undefined" ? {} : navigator;
  const list = nav.languages || (nav.language ? [nav.language] : []);
  for (const raw of list) {
    const tag = cleanTag(raw);
    if (tag && tag.includes("-") && tag.split("-")[0].toLowerCase() === base) return tag;
  }
  return cleanTag(lng) || undefined;
};

/**
 * Some devices (Linux, headless browsers) report languages like "en_US",
 * "en-US@posix" or "en_US.UTF-8", or just "C". The browser's formatting throws
 * on those, which blanked the Money page. Tidy them into "en-US"; anything
 * still unusable gives null so the caller falls back.
 */
const cleanTag = (tag) => {
  try {
    return Intl.getCanonicalLocales(String(tag || "").replace(/[.@].*$/, "").replace(/_/g, "-"))[0] || null;
  } catch {
    return null;
  }
};

// 1 January 2024 was a Monday, so day i (0 = Monday … 6 = Sunday) is 1 + i.
const MONDAY = Date.UTC(2024, 0, 1, 12);

/** "Friday 9 October" in the given language. */
export const longDate = (date, lng) =>
  new Date(date).toLocaleDateString(regional(lng), { weekday: "long", day: "numeric", month: "long" });

/** "9 Oct 2026" in the given language. */
export const shortDate = (date, lng) =>
  new Date(date).toLocaleDateString(regional(lng), { day: "numeric", month: "short", year: "numeric" });

/** Weekday name for Vale's weekday index (0 = Monday). style: "long" | "short". */
export const weekdayName = (index, lng, style = "long") =>
  new Intl.DateTimeFormat(lng, { weekday: style, timeZone: "UTC" })
    .format(new Date(MONDAY + index * 86_400_000));

/** Clock time like "2:05 pm" / "14:05", in the language's own style. */
export const clockTime = (date, lng) =>
  new Date(date).toLocaleTimeString(regional(lng), { hour: "numeric", minute: "2-digit" });

/** "5 minutes ago" / "hace 5 minutos", from an ISO timestamp. */
export const timeAgo = (iso, lng) => {
  if (!iso) return "";
  const seconds = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  const rtf = new Intl.RelativeTimeFormat(regional(lng), { numeric: "auto", style: "short" });
  if (seconds < 60) return rtf.format(-seconds, "second");
  if (seconds < 3600) return rtf.format(-Math.round(seconds / 60), "minute");
  if (seconds < 86400) return rtf.format(-Math.round(seconds / 3600), "hour");
  return rtf.format(-Math.round(seconds / 86400), "day");
};

/** "Tue, Thu and Sat" — a list joined the way the language joins lists. */
export const listOf = (items, lng) => new Intl.ListFormat(regional(lng), { style: "long", type: "conjunction" }).format(items);

/**
 * Wraps text a person typed (a task name, an email) so it keeps its own
 * direction inside a translated sentence. Without this, "gym" next to a number
 * in an Arabic sentence can swap places with it. Uses invisible Unicode
 * isolate marks (FSI … PDI).
 */
export const isolate = (text) => (text == null || text === "" ? "" : `\u2068${text}\u2069`);

/** Number with up to `digits` decimals, e.g. 6.7 → "6,7" in French. */
export const number = (n, lng, digits = 1) =>
  new Intl.NumberFormat(lng, { maximumFractionDigits: digits }).format(n);

/** File size like "12 kB" / "1,5 Mo", in the language's own units. */
export const fileSize = (bytes, lng) => {
  // Small files show as "0.5 kB" — the browser's short word for bytes reads oddly.
  const [value, unit, digits] = bytes < 1024 * 1024
    ? [bytes / 1024, "kilobyte", bytes < 10 * 1024 ? 1 : 0]
    : [bytes / 1024 / 1024, "megabyte", 1];
  return new Intl.NumberFormat(lng, { style: "unit", unit, unitDisplay: "short", maximumFractionDigits: digits })
    .format(value);
};

/** Minutes as "45 min" or "2h 15m" using the locale's phrases. */
export const duration = (minutes, t) => {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m < 60) return t("common.minutes", { n: m });
  const h = Math.floor(m / 60), rem = m % 60;
  return rem ? t("common.hoursMinutes", { h, m: rem }) : t("common.hours", { n: h });
};

/**
 * English needs "1 day" vs "2 days". Locale files carry a `…One` and a `…Many`
 * phrase for these; languages without that distinction can use the same words
 * for both, or a neutral form like "Days left: {{n}}".
 */
export const tCount = (t, keyBase, n, params = {}) =>
  t(`${keyBase}${n === 1 ? "One" : "Many"}`, { n, ...params });
